import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  collection,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  limit as fsLimit,
  orderBy,
  query,
  where,
} from "firebase/firestore";

import { db } from "../lib/firebase";
import { useCan } from "../lib/auth";
import { tl, date } from "../lib/format";
import {
  buildQueryKeys,
  classifyEmptyResult,
  explainOrderMatch,
  matchesAllKeys,
  pickRarestKey,
  type EmptyVerdict,
  type KeyCounts,
  type OrderSearchSource,
} from "../lib/searchKeys";
import { PageHeader } from "../components/PageHeader";
import { LoadingBlock } from "../components/Spinner";
import { EmptyState } from "../components/EmptyState";
import { OrderStatusBadge } from "../components/OrderStatusBadge";
import { useProductionStageLabels } from "../hooks/useProductionStageLabels";

/**
 * GLOBAL ARAMA.
 *
 * ── ESKİ ÇÖZÜM VE NİÇİN ÇALIŞMIYORDU ────────────────────────────────────────
 * Arama, `orderName` ve `customerFullName` alanları üzerinde `[q, q+]`
 * aralık (ön ek) sorgusuydu. Ölçüldü: 29 gerçek senaryodan yalnızca 7'si
 * çalışıyordu. Üç ayrı kök neden vardı:
 *
 *   1. Aralık sorgusu alanın BAŞINDAN eşleşir. "Ezgi Ataş" siparişini "Ataş"
 *      yazarak bulmak MÜMKÜN DEĞİLDİ.
 *   2. Sorgu ham metinle yapılıyordu; `customerFullName` ise "Ataş" gibi büyük
 *      harfli ve Türkçe karakterli saklanıyor. "ataş" ya da "atas" yazan hiçbir
 *      şey bulamıyordu.
 *   3. Takip numarası, telefon, e-posta ve Trendyol numarası HİÇBİR sorguda
 *      taranmıyordu — o alanlar aramanın kapsamında değildi.
 *
 * ── YENİ ÇÖZÜM ──────────────────────────────────────────────────────────────
 * Her siparişte normalize edilmiş `searchTokens` dizisi durur ve arama
 * `array-contains` ile yapılır. Belirteci yazan kod ile sorguyu üreten kod AYNI
 * dosyadır (`lib/searchKeys` → `functions/src/core/searchKeys`); iki tarafın
 * ayrışması artık derleme hatasıdır, sessiz boş sonuç değil.
 *
 * Firestore aynı sorguda iki `array-contains` kabul etmediği için: en seçici
 * anahtar sunucuda sorgulanır, kalan anahtarlar istemcide AND koşulu olarak
 * süzülür. "Ezgi Ataş" arayan kullanıcı "Ezgi Demir"i görmez.
 *
 * ── BOŞ SONUCUN İKİ AYRI SEBEBİ VAR ─────────────────────────────────────────
 * Bu ekranın son ölçülen hatası: kullanıcı "fatma korun" aradı, ekran
 * "eşleşen kayıt bulunamadı — arama TAM KELİME eşleşir" dedi. Kullanıcı
 * aramayı suçlayıp numarayla da denedi. Gerçek şuydu:
 *
 *   • "fatma"  →  118 siparişte geçiyor
 *   • "korun"  →    0 siparişte geçiyor
 *   • Sipariş eski sistemde VAR (Woo #77552, 08.08.2026) ama yeni sisteme
 *     HİÇ göç etmemiş — göç kaynağı 02.08.2026 tarihli bir CSV dışa aktarımı.
 *
 * Yani kayıt YOKTU; arama doğru çalışıyordu. Üstüne, sorgu en yeni 60 "fatma"
 * siparişini çekip elediği için sonuç ayrıca KIRPILMIŞTI ve bu uyarı boş
 * sonuçta hiç gösterilmiyordu. İki ayrı yanlış aynı ekrana çıkıyordu.
 *
 * Artık `count()` toplaması ile her anahtarın kaç kayıtta geçtiği ÖLÇÜLÜR:
 * sorgu en nadir anahtarla atılır ve boş sonuç "sistemde hiç yok" / "birlikte
 * yok" / "pencere doldu" olarak AYRI AYRI anlatılır (bkz. `classifyEmptyResult`).
 */

/**
 * Firestore'dan çekilen üst sınır. Sonuç bu sayıya DAYANIRSA kullanıcıya
 * açıkça yazılır (CONTRACT §2 — sessiz kırpma yok): eski ekran 10 sonuçta
 * kesiyor ve bunu hiç söylemiyordu, kullanıcı da kaydın olmadığını sanıyordu.
 */
const FETCH_LIMIT = 60;

interface OrderHit extends OrderSearchSource {
  id: string;
  erpStatus?: string;
  erpStatusLabel?: string;
  productionStatus?: string;
  totals?: { total?: number };
  createdAt?: { toDate: () => Date } | null;
  searchTokens?: string[];
  /** Bu satır neyle eşleşti — "takip no", "müşteri adı"… */
  reasons: string[];
}

interface CatalogHit {
  id: string;
  title?: string;
  name?: string;
  sku?: string;
  price?: number;
  unit?: string;
  categoryName?: string;
  searchTokens?: string[];
}

interface Results {
  orders: OrderHit[];
  products: CatalogHit[];
  components: CatalogHit[];
  /** Firestore üst sınıra dayandı — gösterilmeyen kayıt olabilir. */
  capped: boolean;
  /** Her anahtarın TÜM koleksiyonlardaki toplam eşleşme sayısı — ölçüm. */
  counts: KeyCounts;
}

const EMPTY: Results = { orders: [], products: [], components: [], capped: false, counts: {} };

/**
 * Sayım atılacak en fazla anahtar. Serbest metinde anahtar sayısı kelime
 * sayısıdır (ön ek üretilmez), kod aramasında `codeKeys` en çok 4 anahtar
 * verir; 6 pratikte hepsini kapsar. Sayım toplaması okunan belgeyi değil
 * eşleşme sayısını faturalandırır, bu yüzden ucuzdur — ama sınırsız değil.
 */
const MAX_COUNTED_KEYS = 6;

export function Search() {
  const [params] = useSearchParams();
  const q = (params.get("q") ?? "").trim();
  // Parça sonuçlarının bağlantısı role bağlıdır: `/components` üretim rolüne
  // kapalıdır, bağlantı gösterilirse 403 paneline götürür.
  const can = useCan();
  // "Üretimde" yerine "Üretimde (Paketleme)" — arayan kişi siparişi açmadan görsün.
  const stages = useProductionStageLabels();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [res, setRes] = useState<Results>(EMPTY);

  // Anahtarlar sorgunun saf bir fonksiyonu; `useMemo` referansı sabitler ki
  // efekt her render'da değil, yalnızca sorgu değişince çalışsın.
  const keys = useMemo(() => buildQueryKeys(q), [q]);

  useEffect(() => {
    if (q.length < 2 || keys.length === 0) {
      setRes(EMPTY);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);

    const run = async (): Promise<Results> => {
      let capped = false;

      /* ── Anahtar sayımı ──────────────────────────────────────────────────
       * Sorgudan ÖNCE her anahtarın kaç kayıtta geçtiğini ölçeriz. İki işe
       * yarar: (1) sorgu en nadir anahtarla atılır, (2) sonuç boş çıkarsa
       * sebebini TAHMİN etmeyiz, biliriz.
       *
       * Tek anahtarlı aramada sayım atlanabilirdi ama atlanmıyor: "bu numara
       * sistemde hiç yok" cümlesini kurabilmek için 0 olduğunu ölçmek gerekir,
       * yoksa boş sonucu yine "arama bulamadı" diye sunmak zorunda kalırdık.
       */
      const counted = keys.slice(0, MAX_COUNTED_KEYS);
      const counts: Record<string, number> = {};
      await Promise.all(
        counted.map(async (k) => {
          const per = await Promise.all(
            (["orders", "products", "components"] as const).map(async (name) => {
              const agg = await getCountFromServer(
                query(collection(db, name), where("searchTokens", "array-contains", k)),
              );
              return agg.data().count;
            }),
          );
          counts[k] = per.reduce((a, b) => a + b, 0);
        }),
      );

      // En nadir anahtar sunucuda sorgulanır; kalanlar istemcide AND'lenir.
      const primary = pickRarestKey(counted, counts) ?? (keys[0] as string);

      /* ── Siparişler ──────────────────────────────────────────────────── */
      const orderHits = new Map<string, OrderHit>();

      /**
       * Sipariş numarası aynı zamanda doküman kimliğidir (Shopify sipariş
       * kimliği ya da göç edilmiş Woo numarası). Doğrudan okumak hem tek
       * ağ turu hem de belirteçleri henüz yazılmamış bir siparişi de bulur —
       * indeks gecikmesine karşı emniyet kemeri.
       */
      if (/^[A-Za-z0-9_]+$/.test(q)) {
        const d = await getDoc(doc(db, "orders", q));
        if (d.exists()) {
          const data = d.data() as OrderSearchSource;
          orderHits.set(d.id, {
            ...(data as OrderHit),
            id: d.id,
            reasons: ["sipariş kimliği"],
          });
        }
      }

      const orderSnap = await getDocs(
        query(
          collection(db, "orders"),
          where("searchTokens", "array-contains", primary),
          orderBy("createdAt", "desc"),
          fsLimit(FETCH_LIMIT),
        ),
      );
      if (orderSnap.size >= FETCH_LIMIT) capped = true;
      for (const d of orderSnap.docs) {
        const data = d.data() as OrderSearchSource & { searchTokens?: string[] };
        // Firestore yalnızca ilk anahtarı süzdü; geri kalanı burada AND'lenir.
        if (!matchesAllKeys(data.searchTokens, keys)) continue;
        if (orderHits.has(d.id)) continue;
        orderHits.set(d.id, {
          ...(data as OrderHit),
          id: d.id,
          reasons: explainOrderMatch({ ...data, id: d.id }, keys),
        });
      }

      /* ── Ürünler ve parçalar ─────────────────────────────────────────── */
      const catalog = async (name: "products" | "components"): Promise<CatalogHit[]> => {
        const snap = await getDocs(
          query(
            collection(db, name),
            where("searchTokens", "array-contains", primary),
            fsLimit(FETCH_LIMIT),
          ),
        );
        if (snap.size >= FETCH_LIMIT) capped = true;
        return snap.docs
          .map((d) => ({ ...(d.data() as CatalogHit), id: d.id }))
          .filter((c) => matchesAllKeys(c.searchTokens, keys));
      };

      const [products, components] = await Promise.all([catalog("products"), catalog("components")]);

      return { orders: [...orderHits.values()], products, components, capped, counts };
    };

    run()
      .then((r) => {
        if (!cancelled) setRes(r);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : "Arama başarısız.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [q, keys]);

  const total = res.orders.length + res.products.length + res.components.length;

  /**
   * Boş sonuç neden boş? Tahmin değil, sayıma dayanır. Bu metinler doğrudan
   * kullanıcının bir sonraki hamlesini belirler: "sistemde yok" diyorsak eski
   * sisteme bakılacak, "arama daralt" diyorsak yazım denenecek. İkisini
   * karıştıran metin kullanıcıya boşuna zaman kaybettiriyordu.
   */
  const verdict: EmptyVerdict | null =
    total === 0 && keys.length > 0 ? classifyEmptyResult(keys, res.counts, res.capped) : null;

  const emptyText = (): { title: string; description: string } => {
    if (verdict?.kind === "absent") {
      const list = verdict.absentKeys.map((k) => `"${k}"`).join(", ");
      return {
        title: `${list} hiçbir kayıtta geçmiyor.`,
        description:
          "Bu bir arama kısıtı DEĞİL: bu terim sipariş, ürün ve parça kayıtlarının " +
          "hiçbirinde yok. Sipariş eski sistemde açılmış ve yeni sisteme " +
          "aktarılmamış olabilir — sipariş numarasını eski sistemden doğrulayın. " +
          "Yazım hatası ihtimali için ad yerine telefonun 10 hanesini veya " +
          "e-postayı deneyin.",
      };
    }
    if (verdict?.kind === "windowCapped") {
      return {
        title: `"${q}" için gösterilecek kayıt bulunamadı — sonuç listesi sınıra dayandı.`,
        description:
          `Aranan terimlerin her biri kayıtlarda geçiyor ama sunucu en fazla ${FETCH_LIMIT} ` +
          "kayıt döndürdüğü için hepsi taranamadı. Aramayı daraltın: tam sipariş " +
          "numarası, telefonun 10 hanesi veya e-posta adresi kesin sonuç verir.",
      };
    }
    if (verdict?.kind === "noCombination") {
      const detail = keys
        .filter((k) => typeof res.counts[k] === "number")
        .map((k) => `"${k}" ${res.counts[k]} kayıt`)
        .join(", ");
      return {
        title: `"${q}" terimlerinin hepsini birlikte taşıyan kayıt yok.`,
        description:
          (detail ? `Tek tek bakıldığında: ${detail}. ` : "") +
          "Terimler ayrı ayrı var ama aynı kayıtta buluşmuyor. Yalnızca soyadı ya " +
          "da yalnızca sipariş numarasıyla aramayı deneyin.",
      };
    }
    return {
      title: `"${q}" ile eşleşen kayıt bulunamadı.`,
      description:
        "Arama TAM KELİME eşleşir; kelimenin ortasından parça aramaz. " +
        "Deneyebilecekleriniz: sipariş numarasının yalnızca rakamı (1028), " +
        "müşterinin yalnızca soyadı, kargo takip numarasının tamamı, " +
        "telefonun 10 hanesi (5548621121) veya tam SKU.",
    };
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={q ? `"${q}" için sonuçlar` : "Arama"}
        description="Sipariş no, müşteri adı, telefon, e-posta, kargo takip no, Trendyol no, ürün adı ve SKU aranır."
      />

      {q.length < 2 || keys.length === 0 ? (
        <EmptyState
          title="En az 2 karakter yazın"
          description="Sipariş numarası, müşteri adı, telefon, kargo takip numarası, e-posta, ürün adı veya SKU yazabilirsiniz."
        />
      ) : error ? (
        <div role="alert" className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-900">
          {error}
        </div>
      ) : loading ? (
        <LoadingBlock />
      ) : total === 0 && verdict?.kind === "absent" ? (
        /**
         * "Kayıt sistemde YOK" bilgisi bir boş liste değil, BULGUdur: kullanıcı
         * aramayı denemeyi bırakıp eski sisteme bakmalıdır. Sarı "boş liste"
         * kutusuyla aynı görünürse yine arama kısıtı sanılır, o yüzden ayrı ve
         * daha güçlü bir kutuda verilir.
         */
        <div role="status" className="rounded-lg border border-red-300 bg-red-50 px-4 py-4 text-sm text-red-900">
          <p className="font-semibold">{emptyText().title}</p>
          <p className="mt-1">{emptyText().description}</p>
        </div>
      ) : total === 0 ? (
        // Kalan iki sebep de "arama tarafı" — sarı kutu doğru tonu verir.
        <EmptyState tone="warning" {...emptyText()} />
      ) : (
        <div className="space-y-8">
          {res.capped ? (
            <div
              role="status"
              className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
            >
              Sonuç listesi {FETCH_LIMIT} kayıtta sınırlandı — eşleşen başka kayıtlar olabilir.
              Aramayı daraltın (ör. ad yerine ad + soyad, ya da tam sipariş numarası).
            </div>
          ) : null}

          {res.orders.length > 0 ? (
            <section aria-labelledby="ara-siparis">
              <h2 id="ara-siparis" className="text-sm font-semibold uppercase tracking-wide text-gray-600">
                Siparişler ({res.orders.length})
              </h2>
              <ul role="list" className="mt-3 divide-y divide-gray-200 overflow-hidden rounded-lg bg-white ring-1 ring-gray-200">
                {res.orders.map((o) => (
                  <li key={o.id}>
                    <Link to={`/orders/${o.id}`} className="flex items-center justify-between gap-4 px-4 py-3 hover:bg-primary-50">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-gray-900">
                          <span className="mr-2 text-gray-600">{o.orderName ?? o.id}</span>
                          {o.customerFullName ?? "—"}
                        </p>
                        <p className="mt-0.5 text-xs text-gray-600">
                          {date(o.createdAt)}
                          {o.reasons.length > 0 ? (
                            // Kullanıcı "bu sipariş neden çıktı?" diye sormamalı.
                            <span className="ml-2 text-gray-500">· {o.reasons.join(", ")} ile bulundu</span>
                          ) : null}
                        </p>
                      </div>
                      {/* ★ Telefonda sağ blok satırın en fazla yarısını alır: "Üretimde
                          (Tamamlanan Üretim)" rozeti daraltılamaz bir blokta müşteri adını
                          2-5 harfe indiriyor, tarihi rozetin altına itiyordu. Rozet
                          sığmazsa KENDİ İÇİNDE iki satıra kırılır. */}
                      <div className="flex max-w-[50%] shrink-0 flex-col items-end gap-1 sm:max-w-none sm:flex-row sm:items-center sm:gap-3">
                        <OrderStatusBadge
                          erpStatus={o.erpStatus}
                          erpStatusLabel={o.erpStatusLabel}
                          productionStatus={o.productionStatus}
                          stages={stages}
                        />
                        <span className="text-sm tabular-nums text-gray-900">{tl(o.totals?.total)}</span>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {res.products.length > 0 ? (
            <section aria-labelledby="ara-urun">
              <h2 id="ara-urun" className="text-sm font-semibold uppercase tracking-wide text-gray-600">
                Ürünler ({res.products.length})
              </h2>
              <ul role="list" className="mt-3 divide-y divide-gray-200 overflow-hidden rounded-lg bg-white ring-1 ring-gray-200">
                {res.products.map((p) => (
                  <li key={p.id}>
                    <Link to={`/products/${p.id}`} className="flex items-center justify-between gap-4 px-4 py-3 hover:bg-primary-50">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-gray-900">{p.title ?? p.name ?? p.id}</p>
                        <p className="mt-0.5 text-xs text-gray-600">SKU: {p.sku ?? "—"}</p>
                      </div>
                      <span className="shrink-0 text-sm tabular-nums text-gray-900">{tl(p.price)}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {res.components.length > 0 ? (
            <section aria-labelledby="ara-parca">
              <h2 id="ara-parca" className="text-sm font-semibold uppercase tracking-wide text-gray-600">
                Parçalar ({res.components.length})
              </h2>
              <ul role="list" className="mt-3 divide-y divide-gray-200 overflow-hidden rounded-lg bg-white ring-1 ring-gray-200">
                {res.components.map((c) => {
                  const body = (
                    <>
                      <p className="truncate text-sm font-semibold text-gray-900">{c.name ?? c.id}</p>
                      <span className="shrink-0 text-xs text-gray-600">
                        {c.categoryName ?? "—"} · {c.unit ?? "adet"}
                      </span>
                    </>
                  );
                  return (
                    <li key={c.id}>
                      {/*
                        ★ BAĞLANTI ROLE GÖRE KURULUR.

                        Arama ekranı ÜÇ ROLE de açıktır ve `components` koleksiyonunu
                        herkes okuyabilir (Rules: `allow get, list: if isActive()`), ama
                        `/components` rotası yalnızca boss + office'e açıktır. Üretim
                        rolündeki kullanıcı bir parça arayıp sonuca tıkladığında
                        "Bu ekrana erişim yetkiniz yok" paneline düşüyordu: sonuç
                        doğruydu, gidilecek yer yoktu.

                        Yetkisi olmayana bağlantı GÖSTERİLMEZ — sonucun kendisi
                        (ad, kategori, birim) yine görünür, çünkü bilgi zaten
                        okunabilir; kırık olan yalnızca hedefti.
                      */}
                      {can.isStaff ? (
                        <Link
                          to="/components"
                          className="flex items-center justify-between gap-4 px-4 py-3 hover:bg-primary-50"
                        >
                          {body}
                        </Link>
                      ) : (
                        <div className="flex items-center justify-between gap-4 px-4 py-3">{body}</div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          ) : null}
        </div>
      )}
    </div>
  );
}

export default Search;
